import {Component, OnInit, Output, EventEmitter, Input} from '@angular/core';
import {Donation} from "../donation/donation";
import {ShopCartService} from "./shop.cart.service";
import {Product} from "./shop.product.model";
import {FormControl, FormGroup} from "@angular/forms";
import {OptionService} from "../option/option.service";
import {ContactComponent} from "../contact/contact.component";
import {ShopCartItem} from "./shop.cart.item";
import { PRODUCT_ID_OPTIONAL_DONATION } from './shop.config';
import { ShopProductService } from './shop.product.service';

interface Inventory {
    [uuid: string]: {
        current: number,
        available: number
    };
}
@Component({
    selector: 'shop-cart',
    templateUrl: './shop.cart.component.html',
    providers: []
})
export class ShopCartComponent implements OnInit {

    /**
     * The current donation
     */
    @Input() donation: Donation;

    /**
     * current widgetsize
     */
    @Input() size: string;

    /**
     * Output parameter for changing the step
     * @type {EventEmitter}
     */
    @Output() stepChange: EventEmitter<string> = new EventEmitter();

    /**
     * List of items to be shown in the cart overview.
     */
    items: ShopCartItem[];

    /**
     * the overall price of the whole cart.
     */
    overallPrice: number = 0;

    /**
     * number of gift certificates the user wants
     */
    giftCount: number = 0;

    /**
     * formgroup for validating the cart form.
     */
    form: FormGroup;

    /**
     * indicator if the form is valid or not.
     */
    formValid: boolean;

    /**
     * Show or hide the gift donation selection.
     */
    showGiftDonation: boolean = false;

    /**
     * Hold the products for the gift selection
     */
    productsForGift: [any, string][] = [];

    /**
     * are the contact data mandatory or not
     */
    contactMandatory: boolean = false;

    /**
     * Inventory for the current cart items
     */
    inventory: Inventory = {};

    /**
     * donation copy for the optional amount
     */
    optionalDonation: Donation;

    /**
     * format for the price with or without decimals
     */
    priceFormat: string = '1.0-0';

    /**
     * constructor
     * @param cartService
     * @param optionService
     * @param shopProductService
     */
    constructor(private cartService: ShopCartService, private optionService: OptionService, private shopProductService: ShopProductService) {}

    /**
     * @inheritDoc
     */
    ngOnInit(): void {
        // check if the contact data should be requested manually
        this.contactMandatory = ContactComponent.isContactMandatory();

        this.items = this.cartService.getItems();

        this.cartService.itemsUpdated.subscribe(() => this.updateItems());
        this.updateItems();

        // formgroup for the gift donation
        this.form = new FormGroup({});

        // init toggle if the button is actiove or not
        this.formValid = this.cartService.getItemCount() > 0;

        // show or hide the gift donation seletion
        this.showGiftDonation = this.optionService.getOption('gift_donation') === "mandatory" || this.optionService.getOption('gift_donation') === "optional";

        // update donation amount, when entering the cart
        this.donation.amount = this.cartService.getPrice();

        // Subscribe to getDecimalsShownCart observable to detect changes
        this.shopProductService.getDecimalsShownCart().subscribe((decimalsShown) => {
            this.priceFormat = decimalsShown ? '1.2-2' : '1.0-0';
        });

        if (this.hasOptionalDonation()) {
            // copy the donation for the optional amount
            this.optionalDonation = new Donation();
            this.optionalDonation.copyData(this.donation);
            
            // remove optional product from the cart
            this.cartService.removeItem(this.getOptionalProduct());

            // reset amount by removing the cart price from the donation amount after the optional product is removed
            this.optionalDonation.amount = Math.max(0, this.optionalDonation.amount - this.cartService.getPrice());            
        }
    }

    /**
     * eventhandler when the validation of the form changed.
     * @param valid
     */
    onFormChanged(valid: boolean): void {
        // set this via timeout, otherwise the changedetection throws errors.
        setTimeout(() => {
            this.formValid = this.cartService.getItemCount() > 0 && this.form.valid === true;
        }, 0)

    }

    /**
     * Updates the items and the overall price
     */
    updateItems(): void {
        this.overallPrice = this.cartService.getPrice();
        this.items = this.cartService.getItems();
        this.formValid = this.cartService.getItemCount() > 0;
        this.updateProductsForGift();
        this.setInventory();
    }

    /**
     * show the payment step
     */
    nextStep(): void {
        // add the optional donation as a product to the cart
        if (this.hasOptionalDonation()) {
            const optionalProduct = this.getOptionalProduct();
            optionalProduct.price = this.optionalDonation.amount;
            if (this.optionalDonation.amount > 0) {
                this.cartService.addItem(optionalProduct, 1);
            }
        }

        if(this.formValid) {
            if(this.contactMandatory === true) {
                this.stepChange.emit('contact-mandatory');
            }
            else {
                this.stepChange.emit(this.size === 'small' && this.donation.isAmountZero() === false ? 'payment-select' : 'payment');
            }
        }
        else {
            console.warn("form is not valid", this.form);
            // form is not valid
            for(let name in this.form.controls) {
                (<FormControl>this.form.controls[name]).markAsTouched({ onlySelf: true });
            }
        }
    }

    /**
     * change the count of s single product to a fixed value
     * @param product
     * @param count
     */
    changeItemCount(newCount: number, item: ShopCartItem): void {
        const product = item.product;

        this.cartService.setItemCount(product, newCount, item.uuid);        
    }

    /**
     * removes the items from the cart.
     * @param product
     */
    removeItem(item: ShopCartItem): void {
        const product = item.product;
        this.cartService.removeItem(product, item.uuid);
    }

    /**
     * Updates the list of products for the gift config.
     */
    updateProductsForGift(): void {
        this.productsForGift = [];
        for(let item of this.items) {
            this.productsForGift.push([item.product.id, item.product.name]);
        }
    }

    /**
     * UPdate the number of gifts, based on the selection in the component.
     * @param giftCount
     */
    updateGiftCount(giftCount: number): void {
        // update this with timeout to avoid changedetection errors if this is change during onInit
        setTimeout(() => {
            this.cartService.setExtraFactor(giftCount > 1? giftCount : 1);
            this.cartService.setGiftCount(giftCount);
            this.giftCount = giftCount;
        }, 0);
    }

    /**
     * returns the path to the product image.
     * @param product
     */
    getProuctImage(product: Product): string {
        if(product.images.length > 0) {
            return this.optionService.getImageDirectory() + '/' + product.images[0].file;
        }
        return null;
    }

    /**
     * Set the inventory for the current cart items
     * this contains the current count and the available count for each item
     */
    setInventory() {
        this.inventory = {};

        // get the count of each product aggregated by product id
        const totalProductCounts : {[key:string]: number} = this.items.reduce((acc, item) => {
            acc[item.product.id] = (acc[item.product.id] || 0) + item.count;
            return acc;
        }, {});

        // set the inventory for each item
        this.inventory = this.items.reduce((inventory, item) => {
            const product = item.product;
            const maxCount = product.getMaxCount();
            const totalProductCount = totalProductCounts[product.id] || 0;
            const availableCount = maxCount > 0 ? maxCount - totalProductCount : 0;

            inventory[item.uuid] = {
                current: item.count,
                available: availableCount
            }
            return inventory;
        }, {});
    }

    /**
     * returns if the optional donation is active
     */
    hasOptionalDonation(): boolean {
        return this.optionService.getOption('has_shop_donation') === true;
    }

    /**
     * create optional product
     */
    getOptionalProduct(): Product {
        const optionalProduct = new Product();
        optionalProduct.id = PRODUCT_ID_OPTIONAL_DONATION;
        optionalProduct.name = this.optionService.getOption('shop_donation_productname');
        optionalProduct.categories = [];

        return optionalProduct;
    }
}
